/**
 * PORTED — RO-3-00 / #44398.
 * Origin: `excellent/app/_home/signals-list.tsx`.
 *
 * Changes: `"use client"` dropped; `useRouter` → `useNavigate` from the seam.
 *
 * ⚠️ `rows` is a prop and stays one. The comment below ("the server maps the live
 * `AttentionItem[]` to these serializable rows") describes a mapping that runs in
 * a server component here — `_home/home-screen.tsx`, which is in the BLOCKED set
 * (shared-render.md §4, it reaches `src/dashboard/pulse-spine.ts` and
 * `src/projects/tenant.ts`). So this island is ported and correct, and nothing in
 * this host can feed it real rows until that mapping is behind a contract.
 */

import { WorkList, type WorkRowModel, type WorkAttention, type Intent } from "@excellent/ui";
import { useNavigate } from "@excellent/ui/router";
import { ArrowRight } from "@excellent/ui/icons";

/**
 * Home "Signals" — the workspace attention union rendered through the shared
 * `WorkList`, the same canonical row the Agents overview and the Work inbox use.
 * Each row carries a SEVERITY, expressed as the WorkRow ATTENTION MARK (the mark
 * means "a person is needed") rather than a bare dot, with the "why" as a muted
 * subtitle and the CTA on hover. The server maps the live `AttentionItem[]` to
 * these serializable rows; this island only renders them.
 */

export interface SignalRow {
  id: string;
  intent: Intent;
  title: string;
  subtitle?: string;
  href?: string | null;
  cta?: string;
}

/**
 * Severity → the attention mark. The mark is only for rows that WANT a person:
 * `info`/`success`/`neutral` get none (nothing is at risk). Severity is the one
 * categorical dimension this heterogeneous union carries, so the label speaks it
 * — the same "3 = act now" semantics the attention rail and the review StatCard
 * use — since the raw `AttentionItem` has no kind or per-signal action verb to
 * name instead. These stay NOTICES, not the blocked "Needs your …" treatment:
 * that label must be a specific act (a verify, a reply), and this feed's only CTA
 * is a generic "Open" — inventing an action for every row would be a lie.
 */
function signalAttention(intent: Intent): WorkAttention | undefined {
  if (intent === "danger") return { label: "Act now", level: "danger" };
  if (intent === "warning") return { label: "Review soon", level: "warning" };
  return undefined;
}

export function SignalsList({ rows }: { rows: SignalRow[] }) {
  const navigate = useNavigate();
  const model: WorkRowModel[] = rows.map((r) => ({
    id: r.id,
    // No kind glyph and no date: a signal is a heterogeneous attention read with
    // neither a type nor a timestamp in its data, so both slots stay empty and
    // the severity mark leads.
    title: r.title,
    subtitle: r.subtitle,
    attention: signalAttention(r.intent),
    actions: r.href
      ? [{ icon: ArrowRight, label: r.cta ?? "Open", onClick: () => navigate.push(r.href!) }]
      : [],
    onOpen: r.href ? () => navigate.push(r.href!) : undefined,
  }));
  return (
    <WorkList rows={model} empty="You're all clear — nothing needs your attention." />
  );
}
